// src/components/Dashboard/CoachDashboard/TodaysAgenda.jsx
// Today's Agenda - Hero section showing live and upcoming events for selected day

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { 
  FaCalendarDay, FaClock, FaPlay, FaMapMarkerAlt,
  FaCheckCircle, FaTimesCircle, FaQuestionCircle, FaArrowRight
} from 'react-icons/fa';
import styles from './TodaysAgenda.module.css';

const TodaysAgenda = ({ program, refreshTrigger }) => {
  const navigate = useNavigate();
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [rsvpData, setRsvpData] = useState({});

  // Fetch all events for the week
  useEffect(() => {
    const fetchEvents = async () => {
      if (!program?._id) {
        setAllEvents([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        const headers = token ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        } : {};
        
        const response = await fetch(`${backendUrl}/api/events/${program._id}`, { headers });
        
        if (!response.ok) {
          setAllEvents([]);
          return;
        }

        const events = await response.json();
        setAllEvents(events || []);
      } catch (error) {
        console.error('Error fetching events:', error);
        setAllEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [program?._id, refreshTrigger]);

  // Reset to today when program changes
  useEffect(() => {
    setSelectedDate(new Date());
  }, [program?._id]);

  // Get events for selected date
  const selectedDayEvents = useMemo(() => {
    // Use date string comparison for reliability
    const selectedDateStr = selectedDate.toDateString();
    
    return allEvents
      .filter(event => {
        const eventDate = new Date(event.dateTime?.start);
        return eventDate.toDateString() === selectedDateStr;
      })
      .sort((a, b) => new Date(a.dateTime?.start) - new Date(b.dateTime?.start));
  }, [allEvents, selectedDate]);

  // Fetch RSVP data for selected day events
  useEffect(() => {
    const fetchRsvpData = async () => {
      if (selectedDayEvents.length === 0) {
        setRsvpData({});
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const auth = getAuth();
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      
      if (!token) return;

      const rsvpPromises = selectedDayEvents.map(async (event) => {
        try {
          const response = await fetch(`${backendUrl}/api/events/response/${event._id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const data = await response.json();
            return { eventId: event._id, summary: data.summary };
          }
        } catch (error) {
          // Silently fail for individual events
        }
        return { eventId: event._id, summary: null };
      });

      const results = await Promise.all(rsvpPromises);
      const newRsvpData = {};
      results.forEach(r => {
        if (r.summary) {
          newRsvpData[r.eventId] = r.summary;
        }
      });
      setRsvpData(newRsvpData);
    };

    fetchRsvpData();
  }, [selectedDayEvents]);

  // Get week days with event counts
  const weekDays = useMemo(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    
    return days.map((dayName, index) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + index);
      date.setHours(0, 0, 0, 0);
      
      const dateStr = date.toDateString();
      
      const dayEvents = allEvents.filter(e => {
        const eventDate = new Date(e.dateTime?.start);
        return eventDate.toDateString() === dateStr;
      });
      
      const isToday = dateStr === today.toDateString();
      const isSelected = dateStr === selectedDate.toDateString();
      
      return { 
        dayName, 
        date, 
        dayNum: date.getDate(),
        isToday, 
        isSelected,
        eventCount: dayEvents.length,
        hasEvent: dayEvents.length > 0
      };
    });
  }, [allEvents, selectedDate]);

  const handleDayClick = (date) => {
    // Create a new Date object to ensure React detects the state change
    const newDate = new Date(date.getTime());
    setSelectedDate(newDate);
  };

  const formatTime = (event) => {
    const date = new Date(event.dateTime?.start);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType?.toLowerCase()) {
      case 'game':
      case 'match':
        return '🏆';
      case 'practice':
      case 'training':
        return '⚽';
      case 'meeting':
        return '📋';
      default:
        return '📅';
    }
  };

  const isLiveEvent = (event) => {
    return event.status === 'Live' || event.status === 'live';
  };

  const handleEventClick = (event) => {
    // Navigate to ProgramLayout events page with event highlighted
    navigate(`/program/${program._id}/events?eventId=${event._id}`);
  };

  const handleViewAllEvents = () => {
    // Navigate to ProgramLayout events page
    navigate(`/program/${program._id}/events`);
  };

  const getDateLabel = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (selected.getTime() === today.getTime()) {
      return 'Today';
    } else if (selected.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else if (selected.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    }
    return selected.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const formattedDate = selectedDate.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  const liveEvents = selectedDayEvents.filter(isLiveEvent);
  const upcomingEvents = selectedDayEvents.filter(e => !isLiveEvent(e));
  const isToday = new Date().toDateString() === selectedDate.toDateString();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FaCalendarDay className={styles.headerIcon} />
          <h2 className={styles.headerTitle}>
            {getDateLabel()}'s Agenda
          </h2>
        </div>
        <span className={styles.dateLabel}>{formattedDate}</span>
      </div>

      <div className={styles.content}>
        {/* Compact Week Selector */}
        <div className={styles.weekSelector}>
          <span className={styles.weekLabel}>Day:</span>
          <div className={styles.weekDays}>
            {weekDays.map((day, idx) => (
              <button 
                key={idx}
                className={`${styles.dayButton} ${day.isToday ? styles.today : ''} ${day.isSelected ? styles.selected : ''} ${day.hasEvent ? styles.hasEvent : ''}`}
                onClick={() => handleDayClick(day.date)}
                title={`${day.dayName} - ${day.eventCount} event${day.eventCount !== 1 ? 's' : ''}`}
              >
                <span className={styles.dayName}>{day.dayName}</span>
                <span className={styles.dayNum}>{day.dayNum}</span>
                {day.hasEvent && <span className={styles.eventBadge}></span>}
              </button>
            ))}
          </div>
        </div>

        {/* Events for Selected Day */}
        {selectedDayEvents.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>{isToday ? '☀️' : '📅'}</span>
            <p>No events scheduled for {getDateLabel().toLowerCase()}</p>
            <span className={styles.emptyHint}>
              {isToday ? 'Enjoy your rest day!' : 'Click another day to see events'}
            </span>
          </div>
        ) : (
          <div className={styles.eventsGrid}>
            {/* Live Events */}
            {liveEvents.map(event => (
              <div 
                key={event._id} 
                className={`${styles.eventCard} ${styles.liveEvent}`}
                onClick={() => handleEventClick(event)}
              >
                <div className={styles.liveBadge}>
                  <FaPlay /> LIVE
                </div>
                <div className={styles.eventHeader}>
                  <span className={styles.eventIcon}>{getEventTypeIcon(event.type)}</span>
                  <span className={styles.eventType}>{event.type}</span>
                </div>
                <h3 className={styles.eventTitle}>{event.title}</h3>
                {event.liveSession?.score && (
                  <div className={styles.liveScore}>
                    <span>{event.liveSession.score.home}</span>
                    <span className={styles.scoreDivider}>-</span>
                    <span>{event.liveSession.score.away}</span>
                  </div>
                )}
                <button className={styles.eventAction}>
                  Open Scorecard <FaArrowRight />
                </button>
              </div>
            ))}

            {/* Upcoming/Past Events */}
            {upcomingEvents.map(event => (
              <div 
                key={event._id} 
                className={styles.eventCard}
                onClick={() => handleEventClick(event)}
              >
                <div className={styles.eventHeader}>
                  <span className={styles.eventIcon}>{getEventTypeIcon(event.type)}</span>
                  <span className={styles.eventTime}>
                    <FaClock /> {formatTime(event)}
                  </span>
                </div>
                <h3 className={styles.eventTitle}>{event.title}</h3>
                {event.location?.name && (
                  <div className={styles.eventLocation}>
                    <FaMapMarkerAlt />
                    <span>{event.location.name}</span>
                  </div>
                )}
                <div className={styles.rsvpSummary}>
                  <span className={styles.rsvpGoing}>
                    <FaCheckCircle /> {rsvpData[event._id]?.going || 0}
                  </span>
                  <span className={styles.rsvpMaybe}>
                    <FaQuestionCircle /> {rsvpData[event._id]?.tbd || 0}
                  </span>
                  <span className={styles.rsvpNotGoing}>
                    <FaTimesCircle /> {rsvpData[event._id]?.notGoing || 0}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View All Events Button */}
        <div className={styles.footerRow}>
          <button className={styles.viewAllBtn} onClick={handleViewAllEvents}>
            View All Events <FaArrowRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TodaysAgenda;
